// src/modal/MapModal.tsx
import React from 'react';
import moment from 'moment';

interface SitePersonnel {
  personalID: number;
  firstName: string;
  lastName: string;
  position?: string;
  timeIn?: string;
  timeOut?: string;
  lunchIn?: string;
  lunchOut?: string;
}

interface MapSiteData {
  siteID: number;
  siteNumber: number;
  siteName: string;
  address1?: string;
  address2?: string;
  address3?: string;
  phone?: string;
  county?: string;
  principal?: string;
  latitude?: number;
  longitude?: number;
  attendanceCount: number;
  totalPersonnelCount: number;
}

interface SitePersonnelDetail {
  siteID: number;
  siteNumber: number;
  siteName: string;
  address1?: string;
  phone?: string;
  county?: string;
  principal?: string;
  latitude?: number;
  longitude?: number;
  personnel: SitePersonnel[];
}

// Define the types for the props
interface MapModalProps {
  isOpen: boolean;
  closeModal: () => void;
  selectedSiteData: MapSiteData | null;
  personnelDetails?: SitePersonnelDetail | null;
}

const MapModal: React.FC<MapModalProps> = ({ isOpen, closeModal, selectedSiteData, personnelDetails }) => {
  if (!isOpen || !selectedSiteData) return null;

  const presentCount = personnelDetails?.personnel?.filter((staff) => {
    return staff.timeIn;
  }).length || 0;

  const totalPersonnel = personnelDetails?.personnel?.length || selectedSiteData.totalPersonnelCount;

  function calculateBreakTime(lunchIn: string | undefined, lunchOut: string | undefined) {
    if (!lunchIn || !lunchOut) {
      return null;
    }
    const lunchInTime = moment(lunchIn, 'HH:mm:ss');
    const lunchOutTime = moment(lunchOut, 'HH:mm:ss');
    return lunchOutTime.diff(lunchInTime, 'minutes');
  }

  return (
    <div
      className={`modal fade show editAsignment mapInfoModal align-items-xl-center align-items-lg-start overflow-auto align-items-md-center align-items-start justify-content-center`}
      id="Filter"
      aria-labelledby="FilterLabel"
      aria-hidden="true"
    >
      <div className="modal-dialog w-100">
        <div className="modal-content p-4">
          <div className="modal-body p-lg-2 p-md-2 p-0">
            <div className="formCard mt-0 p-0 border-0">
              <div className="FormTitleWithdivider position-relative d-flex align-items-center gap-3 mb-lg-3 mb-md-3 mb-3">
                <h3 className="text-nowrap formTitle m-0">{selectedSiteData.siteNumber}  {selectedSiteData.siteName}</h3>
                <hr className="w-100" />
                <div className="editForm" onClick={closeModal} style={{ cursor: 'pointer' }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    style={{ cursor: 'pointer' }}
                  >
                    <path
                      d="M19 5.5L5 19.5M19 19.5L5 5.5"
                      stroke="black"
                      strokeWidth="2"
                      strokeLinecap="round"
                    ></path>
                  </svg>
                </div>
              </div>

              {/* Site Information */}
              <div className="mb-4">
                <div className="row">
                  {selectedSiteData.address1 && (
                    <div className="col-12 mb-2">
                      <label><strong>Address:</strong> {selectedSiteData.address1}, {selectedSiteData.county || ''}</label>
                    </div>
                  )}
                  {selectedSiteData.phone && (
                    <div className="col-12 mb-2">
                      <label><strong>Phone:</strong> {selectedSiteData.phone}</label>
                    </div>
                  )}
                  {selectedSiteData.principal && (
                    <div className="col-12 mb-2">
                      <label><strong>Principal:</strong> {selectedSiteData.principal}</label>
                    </div>
                  )}
                </div>
              </div>

              <div className="clockE mt-4">
                <div className="mb-3">
                  <label className="d-flex align-items-center justify-content-between gap-3 fw-bold">
                    <span>Today's Attendance:</span>
                    <span>{presentCount}/{totalPersonnel}</span>
                  </label>
                </div>

                {personnelDetails ? (
                  <table className='table w-100 m-0'>
                    <thead>
                      <tr>
                        <th style={{ verticalAlign: 'middle' }} className='text-start text-nowrap'>
                          <label>Date: <b>{moment().format('DD-MM-YYYY')}</b></label>
                        </th>
                        <th style={{ verticalAlign: 'middle' }} className='text-center text-nowrap'>
                          <label>Attendance Status</label>
                        </th>
                        <th style={{ verticalAlign: 'middle' }} className='text-center text-nowrap'>
                          <label>Check In Time</label>
                        </th>
                        <th style={{ verticalAlign: 'middle' }} className='text-center text-nowrap'>
                          <label>Check Out Time</label>
                        </th>
                        {/* <th style={{ verticalAlign: 'middle' }} className='text-center text-nowrap'>
                          <label>Total Break Time</label><br />
                          <span style={{ fontSize: '12px' }}>(In Minutes)</span>
                        </th> */}
                      </tr>
                    </thead>
                    <tbody>
                      {personnelDetails?.personnel?.map((staff, index) => {
                        const status = staff.timeIn ? "Present" : "Absent";
                        
                        return (
                          <tr key={staff.personalID}>
                            <td style={{ verticalAlign: 'middle' }} className='text-nowrap'>
                              <label>Employee {`${index + 1}`} : <b> {`${staff.firstName} ${staff.lastName}`}</b></label><br />
                              <label>Position: <b>{staff.position || 'N/A'}</b></label>
                            </td>
                            <td style={{ verticalAlign: 'middle' }} className='text-center'>
                              <label>{status}</label>
                            </td>
                            <td style={{ verticalAlign: 'middle' }} className='text-center'>
                              <label>{staff?.timeIn ? moment(staff.timeIn, 'HH:mm:ss').format('hh:mm A') : 'N/A'}</label>
                            </td>
                            <td style={{ verticalAlign: 'middle' }} className='text-center'>
                              <label>{staff?.timeOut ? moment(staff.timeOut, 'HH:mm:ss').format('hh:mm A') : 'N/A'}</label>
                            </td>
                            {/* <td style={{ verticalAlign: 'middle' }} className='text-center'>
                              <label>
                                {calculateBreakTime(staff?.lunchIn, staff?.lunchOut) !== null ? (
                                  <label>{calculateBreakTime(staff?.lunchIn, staff?.lunchOut)} Min</label>
                                ) : (
                                  'N/A'
                                )}
                              </label>
                            </td> */}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                ) : (
                  <div className="text-center py-3">
                    <label>Loading personnel details...</label>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapModal;
